import { useLayoutMode } from '#app/hooks/useLayoutMode.js';
import {
  type MultiColumnLayoutMode,
  type ThemeWithMultiColumnLayoutMode,
} from '#app/types/multiColumnLayoutMode.js';
import { createContext, type ReactNode, useMemo } from 'react';
import { ThemeProvider } from 'styled-components';

type WorkTabLayoutContextProviderProps = {
  readonly children: ReactNode;
};

type WorkTabLayoutState = {
  layoutMode: MultiColumnLayoutMode;
};

const WorkTabLayoutContext = createContext<WorkTabLayoutState>({
  layoutMode: 'ONE_COLUMN',
});

export const WorkTabLayoutContextProvider = ({
  children,
}: WorkTabLayoutContextProviderProps) => {
  // Similar to OpportunitiesLayoutContext and FeedLayoutContext
  const ONE_COLUMN_MQ = '(max-width: 999px) and (min-width: 0px)';
  const TWO_COLUMN_MQ = '(max-width: 1329px) and (min-width: 1000px)';
  const TWO_COLUMN_AND_ABOVE_MQ = '(min-width: 1000px)';
  const THREE_COLUMN_MQ = '(min-width: 1330px)';

  const layoutMode: MultiColumnLayoutMode = useLayoutMode(
    [ONE_COLUMN_MQ, TWO_COLUMN_MQ, THREE_COLUMN_MQ],
    ['ONE_COLUMN', 'TWO_COLUMN', 'THREE_COLUMN'],
    'ONE_COLUMN',
  );

  /* eslint-disable perfectionist/sort-objects */
  const multiColumnLayoutMode = {
    mediaQueries: {
      oneColumn: `@media only screen and ${ONE_COLUMN_MQ}`,
      twoColumn: `@media only screen and  ${TWO_COLUMN_MQ}`,
      threeColumn: `@media only screen and ${THREE_COLUMN_MQ}`,
      twoColumnAndAbove: `@media only screen and  ${TWO_COLUMN_AND_ABOVE_MQ}`,
    },
  };
  /* eslint-enable perfectionist/sort-objects */

  const context = useMemo(() => {
    return {
      layoutMode,
    };
  }, [layoutMode]);

  return (
    <ThemeProvider
      theme={(theme): ThemeWithMultiColumnLayoutMode => {
        if (!theme) {
          throw new Error('Theme is undefined');
        }

        return { ...theme, multiColumnLayoutMode };
      }}
    >
      <WorkTabLayoutContext.Provider value={context}>
        {children}
      </WorkTabLayoutContext.Provider>
    </ThemeProvider>
  );
};
